import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { MediaAllegato } from "../../api/media";
import { useProtectedObjectUrl } from "../../hooks/useProtectedObjectUrl";

// ============================================================================
// MediaLightbox — la foto di un allegato a schermo intero.
//
// Overlay a pagina piena invece del componente `Modal`, per la stessa ragione
// per cui non lo usa la mappa a schermo intero (vedi MappaWinkOnCash): il
// Modal ha barra del titolo, padding e scorrimento verticale, che a una foto
// da guardare ingrandita tolgono spazio senza darle niente. Riusa pero' il
// token `z-modal`, cosi' la regola su chi sta sopra a chi resta dichiarata in
// un punto solo (tailwind.config) — e serve davvero, perche' la griglia di
// miniature vive dentro un SlideOver (z-panel).
//
// Il fondo e' scuro e non bianco come la mappa, cosi' una foto con bordi
// chiari si stacca dallo sfondo; ed e' pieno e non translucido, perche' con la
// griglia di miniature che traspariva dietro la foto si faceva fatica a
// guardarla. `primary-dark` e non un nero generico per restare nella palette.
//
// La foto NON viene riscaricata: `useProtectedObjectUrl` la prende dalla cache
// dove l'ha gia' messa la miniatura. Per lo stesso motivo scorrere avanti e
// indietro e' immediato — le miniature restano montate dietro l'overlay,
// quindi ogni foto della griglia ha gia' il suo blob pronto.
// ============================================================================

interface MediaLightboxProps {
  /** le sole foto della griglia da cui si e' aperta: i video non entrano qui */
  foto: MediaAllegato[];
  indice: number;
  onIndice: (indice: number) => void;
  onClose: () => void;
}

export function MediaLightbox({ foto, indice, onIndice, onClose }: MediaLightboxProps) {
  const contenitoreRef = useRef<HTMLDivElement>(null);
  const corrente: MediaAllegato | undefined = foto[indice];
  const piuDiUna = foto.length > 1;

  const { objectUrl, failed } = useProtectedObjectUrl(corrente?.file_url ?? null);

  // La foto aperta puo' sparire sotto i piedi (eliminata altrove, lista
  // ricaricata dopo un upload): invece di rompersi, la lightbox si chiude.
  useEffect(() => {
    if (!corrente) onClose();
  }, [corrente, onClose]);

  // Tastiera: ESC chiude, frecce scorrono. Stesso approccio del Modal, che qui
  // non si puo' riusare direttamente.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (!piuDiUna) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onIndice((indice - 1 + foto.length) % foto.length);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        onIndice((indice + 1) % foto.length);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [indice, foto.length, piuDiUna, onIndice, onClose]);

  // Blocca lo scroll del body mentre la lightbox e' aperta. Salva e ripristina
  // il valore precedente perche' sotto c'e' spesso un SlideOver che lo ha gia'
  // messo a "hidden": chiudendo la foto lo scroll deve restare bloccato per il
  // pannello, non sbloccarsi a meta'.
  useEffect(() => {
    const originale = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originale;
    };
  }, []);

  // Focus sul contenitore, cosi' le frecce funzionano senza dover cliccare.
  useEffect(() => {
    contenitoreRef.current?.focus();
  }, []);

  if (!corrente) return null;

  const etichetta = corrente.nome_file ?? "Foto allegata";

  return createPortal(
    <div
      ref={contenitoreRef}
      role="dialog"
      aria-modal="true"
      aria-label={etichetta}
      tabIndex={-1}
      className="fixed inset-0 z-modal flex flex-col bg-primary-dark outline-none"
    >
      {/* --- Barra: nome file, categoria, posizione, chiusura ---------------- */}
      <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3 text-white">
        {/* Il nome file ha la precedenza sullo spazio: e' quello che identifica
            la foto. La categoria si vede solo da sm in su — su telefono
            rubava tutta la riga al nome, e li' e' comunque ridondante, perche'
            la foto si e' appena aperta dalla riga di quella tipologia. */}
        <div className="flex min-w-0 items-baseline gap-3">
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{etichetta}</span>
          {corrente.categoria && (
            <span className="hidden max-w-[45%] shrink-0 truncate text-xs text-white/50 sm:inline">
              {corrente.categoria}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {piuDiUna && (
            <span className="text-xs tabular-nums text-white/60">
              {indice + 1} / {foto.length}
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="flex items-center gap-1.5 rounded-lg border border-white/25 px-3 py-1.5 text-sm text-white/80 transition hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            Chiudi
          </button>
        </div>
      </div>

      {/* --- Foto ------------------------------------------------------------
          Il click sullo sfondo chiude, quello sulla foto no: e' il gesto che
          ci si aspetta da un overlay del genere. */}
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center p-4"
        onClick={onClose}
      >
        {failed ? (
          <p className="text-sm text-white/60">Impossibile caricare questa foto.</p>
        ) : !objectUrl ? (
          <p className="text-sm text-white/60">Caricamento…</p>
        ) : (
          <img
            src={objectUrl}
            alt={etichetta}
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full object-contain"
          />
        )}

        {piuDiUna && (
          <>
            <FrecciaLightbox
              direzione="precedente"
              onClick={() => onIndice((indice - 1 + foto.length) % foto.length)}
            />
            <FrecciaLightbox
              direzione="successiva"
              onClick={() => onIndice((indice + 1) % foto.length)}
            />
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function FrecciaLightbox({
  direzione,
  onClick,
}: {
  direzione: "precedente" | "successiva";
  onClick: () => void;
}) {
  const isPrecedente = direzione === "precedente";
  const Icona = isPrecedente ? ChevronLeft : ChevronRight;

  return (
    <button
      type="button"
      aria-label={isPrecedente ? "Foto precedente" : "Foto successiva"}
      onClick={(e) => {
        // Senza questo il click arriverebbe allo sfondo, che chiude.
        e.stopPropagation();
        onClick();
      }}
      className={`absolute top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white/70 transition hover:bg-white/20 hover:text-white ${
        isPrecedente ? "left-3" : "right-3"
      }`}
    >
      <Icona className="h-6 w-6" aria-hidden="true" />
    </button>
  );
}
